import React, { useState } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import SEO, { BreadcrumbSchema } from "../components/SEO";
import { api } from "../lib/api";
import { toErrorMessage } from "../lib/errors";
import { FadeUp } from "../components/Motion";
import { Check, Hammer, Shield, Users, Award } from "lucide-react";
import { Link } from "react-router-dom";

const roles = [
  "General Interest", "Project Superintendent", "Field Crew / Laborer",
  "Carpenter / Finish Carpenter", "Project Manager", "Estimator",
  "Office / Admin", "Veterans Transition Program",
];

const years = ["Less than 2", "2-5", "5-10", "10-20", "20+"];

const values = [
  { icon: Hammer, t: "Craft First", d: "We care about the work. Detail matters. Finish matters. The people doing the work matter most." },
  { icon: Shield, t: "Veterans Welcomed", d: "We're a Service-Disabled Veteran-Owned Small Business and deeply proud of our military roots. Veterans will find a home here." },
  { icon: Users, t: "Real Accountability", d: "Decisions made in the field are respected. People are trusted to do their job and lead their trade." },
  { icon: Award, t: "A Career Path", d: "As we grow, our team grows with us. We invest in the people who invest in the work." },
];

const lookingFor = [
  "Superintendents & field leads",
  "Carpenters & finish craftsmen",
  "Project managers & estimators",
  "Skilled tradespeople at every level",
  "Office & administrative support",
  "Military veterans transitioning to civilian work",
];

export default function WorkWithUs() {
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    role_interest: roles[0], experience_years: years[0],
    resume_url: "", message: "",
  });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/careers/apply", form);
      setStatus("success");
      setForm({ first_name: "", last_name: "", email: "", phone: "", role_interest: roles[0], experience_years: years[0], resume_url: "", message: "" });
    } catch (err) {
      setStatus("error");
      setError(toErrorMessage(err));
    }
  };

  const input = "w-full bg-transparent border-b border-ink/20 focus:border-copper py-3 text-ink outline-none transition-colors";
  const label = "text-[10px] tracking-[0.22em] uppercase text-ink/60 mb-1 block";

  return (
    <>
      <SEO
        title="Careers, Trade Partners & Designers"
        description="Join our team, our subcontractor and vendor bid list, or partner with us as an architect or designer in Tampa Bay."
        path="/work-with-us"
      />
      <BreadcrumbSchema items={[{ name: "Home", url: "https://bcpeabody.com/" }, { name: "Work With Us", url: "https://bcpeabody.com/work-with-us" }]} />
      <Nav />

      <section className="pt-32 pb-14 bg-cream-light">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-5 text-copper">Work With Us</div>
          <h1 className="font-serif font-medium text-ink tracking-tight leading-[1.05]" style={{ fontSize: "clamp(34px, 5vw, 60px)" }}>
            Work With Us
          </h1>
          <p className="mt-6 text-ink/75 max-w-2xl" style={{ fontSize: 17 }}>
            We build long relationships with the designers, trades and people who make good projects possible.
          </p>
        </div>
      </section>

      {/* Designers */}
      <section id="designers" data-testid="work-designers" className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4 text-copper">For Architects & Designers</div>
            <h2 className="font-serif font-medium text-ink tracking-tight" style={{ fontSize: "clamp(26px, 3vw, 34px)" }}>Architects & designers</h2>
          </div>
          <div className="col-span-12 md:col-span-8 text-ink/80 leading-relaxed space-y-4">
            <p>We build to your design and protect it in the field. Bring us in early for pricing and constructability, or hand us a finished set. Either way you get one point of contact, clear RFIs and no surprises at the end.</p>
            <Link to="/contact?type=designer" className="btn-copper inline-flex mt-3">Introduce Your Firm →</Link>
          </div>
        </div>
      </section>

      {/* Trades */}
      <section id="trade-partners" data-testid="work-trades" className="bg-cream-light py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] tracking-[0.18em] uppercase mb-4 text-copper">For Trades & Vendors</div>
            <h2 className="font-serif font-medium text-ink tracking-tight" style={{ fontSize: "clamp(26px, 3vw, 34px)" }}>Subcontractors & vendors</h2>
          </div>
          <div className="col-span-12 md:col-span-8 text-ink/80 leading-relaxed space-y-4">
            <p>We're always looking for skilled trades and reliable suppliers across Tampa Bay. Send us your trade, service area, license and insurance information and we'll add you to our bid list.</p>
            <Link to="/contact?type=trade-partner" className="btn-copper inline-flex mt-3">Join Our Bid List →</Link>
          </div>
        </div>
      </section>

      {/* Careers */}
      <section id="careers" data-testid="work-careers" className="bg-cream py-16 md:py-20">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="text-[11px] tracking-[0.18em] uppercase mb-4 text-copper">Careers</div>
          <h2 className="font-serif font-medium text-ink tracking-tight mb-10" style={{ fontSize: "clamp(26px, 3vw, 34px)" }}>Careers</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {values.map((v, i) => (
              <FadeUp key={v.t} delay={i * 60}>
                <div className="p-6 border-t-2 border-copper bg-cream-light h-full">
                  <v.icon className="text-copper mb-4" size={26} strokeWidth={1.3} />
                  <h3 className="font-serif text-xl text-ink mb-2 tracking-tight">{v.t}</h3>
                  <p className="text-ink/75 text-sm leading-relaxed">{v.d}</p>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="mb-14 max-w-3xl">
            <h3 className="font-serif text-2xl text-ink tracking-tight mb-6">Who we're looking for</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-y-3 gap-x-10 text-ink/80">
              {lookingFor.map((r) => (
                <div key={r} className="flex items-start gap-3"><Check className="text-copper shrink-0 mt-1" size={18} /><span>{r}</span></div>
              ))}
            </div>
            <p className="text-ink/60 text-sm italic mt-6">Don't see your role listed? We still want to hear from you.</p>
          </div>

          {status === "success" ? (
            <div data-testid="careers-success" className="bg-cream-light p-10 border-t-2 border-copper text-center max-w-2xl">
              <div className="w-14 h-14 rounded-full border-2 border-copper mx-auto flex items-center justify-center mb-5"><Check className="text-copper" size={28} /></div>
              <h3 className="font-serif text-3xl text-ink mb-3">Application Received.</h3>
              <p className="text-ink/70">Thank you for your interest in BCPeabody. We'll review your application and be in touch.</p>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="careers-form" className="bg-cream-light p-8 md:p-10 border-t-2 border-copper space-y-6 max-w-3xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className={label}>First Name</label><input data-testid="careers-first-name" name="first_name" value={form.first_name} onChange={onChange} required className={input} /></div>
                <div><label className={label}>Last Name</label><input data-testid="careers-last-name" name="last_name" value={form.last_name} onChange={onChange} required className={input} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className={label}>Email</label><input data-testid="careers-email" name="email" type="email" value={form.email} onChange={onChange} required className={input} /></div>
                <div><label className={label}>Phone</label><input data-testid="careers-phone" name="phone" value={form.phone} onChange={onChange} className={input} /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div><label className={label}>Role of Interest</label><select data-testid="careers-role" name="role_interest" value={form.role_interest} onChange={onChange} className={`${input} cursor-pointer`}>{roles.map((r) => <option key={r} value={r}>{r}</option>)}</select></div>
                <div><label className={label}>Years of Experience</label><select data-testid="careers-years" name="experience_years" value={form.experience_years} onChange={onChange} className={`${input} cursor-pointer`}>{years.map((y) => <option key={y} value={y}>{y}</option>)}</select></div>
              </div>
              <div><label className={label}>Resume URL (LinkedIn, Google Drive, Dropbox, etc.)</label><input data-testid="careers-resume" name="resume_url" value={form.resume_url} onChange={onChange} placeholder="https://..." className={input} /></div>
              <div><label className={label}>Tell us about yourself</label><textarea data-testid="careers-message" name="message" value={form.message} onChange={onChange} required rows={5} className={`${input} resize-none`} /></div>
              {error && <div className="text-red-500 text-sm" data-testid="careers-error">{error}</div>}
              <button data-testid="careers-submit" type="submit" disabled={status === "loading"} className="btn-copper disabled:opacity-50">
                {status === "loading" ? "Submitting..." : "Submit Application →"}
              </button>
            </form>
          )}

          <p className="text-sm text-ink/65 mt-8">BCPeabody is an equal opportunity employer.</p>
        </div>
      </section>

      <Footer />
    </>
  );
}
